import React from "react";
import clsx from "clsx";

import WComponent from "../common/WComponent";
import BackofficeService from "../../services/BackofficeService";
import GmailService from "../../services/GmailService";
import UserService from "../../services/UserService";
import UtilityService from "../../services/UtilityService";

import ClientCard from "./ClientCard";
import CalendarView from "./CalendarView";
import CapacityView from "./CapacityView";
import TaskModal from "./modals/TaskModal";
import EmailAutomationModal from "./modals/EmailAutomationModal";
import EmailAccountModal from "./modals/EmailAccountModal";
import TemplateModal from "./modals/TemplateModal";
import AddClientModal from "./modals/AddClientModal";
import { I } from "./common/icons";
import { SERVICES, SERVICE_ORDER, STATUSES, STATUS_ORDER, FREQUENCIES } from "./backofficeConstants";
import { today, addDays, isOverdue, isComplete, periodFor, shiftPeriod, assignees } from "./backofficeUtils";

import "./backoffice.css";

const { alert } = UtilityService;

/**
 * Backoffice: recurring payroll, month-end bookkeeping, AP and ad hoc work per client, with a
 * calendar of due dates and email sends and a capacity report. One page, three views.
 */
class Backoffice extends WComponent {
	constructor(props) {
		super(props);

		const me = UserService.getUserData() || {};
		const hash = (props.location && props.location.hash) || "";

		this.state = {
			loading: true,
			error: null,

			clients: [],
			tasks: [],
			users: [],
			templates: [],
			gmailStatus: { connected: false, gmailAddress: null },
			myUserId: me.userId,

			view: hash === "#calendar" ? "calendar" : hash === "#capacity" ? "capacity" : "clients",
			search: "",
			fService: "",
			fAssignee: "",
			fStatus: "",

			openClients: new Set(),
			selSvc: {},
			periods: {},
			openAccounts: new Set(),
			flashTaskId: null,

			taskModal: null,
			emailModalTaskId: null,
			showAccountModal: false,
			templateModal: null,
			showAddClient: false,
			calendarFocusClientId: null,

			toast: null,
		};
		this.toastTimer = null;
	}

	async componentDidMount() {
		await this.load();
	}

	componentWillUnmount() {
		clearTimeout(this.toastTimer);
	}

	load = async () => {
		await this.update({ loading: true, error: null });
		const [clients, tasks, users, templates, gmailStatus] = await Promise.all([
			BackofficeService.listClients(),
			BackofficeService.listTasks(),
			BackofficeService.listCapacity(),
			BackofficeService.listTemplates(),
			GmailService.getStatus().catch(() => null),
		]);
		if (!clients.ok || !tasks.ok || !users.ok) {
			await this.update({ loading: false, error: clients.error || tasks.error || users.error || "Could not load backoffice data" });
			return;
		}
		const openClients = new Set(this.state.openClients);
		if (!openClients.size && clients.data.length) openClients.add(clients.data[0].id);
		await this.update({
			loading: false,
			clients: clients.data,
			tasks: tasks.data,
			users: users.data,
			templates: templates.ok ? templates.data : [],
			gmailStatus: gmailStatus || { connected: false, gmailAddress: null },
			openClients,
		});
	};

	reloadTasks = async () => {
		const tasks = await BackofficeService.listTasks();
		if (tasks.ok) await this.update({ tasks: tasks.data });
	};

	// ------------------------------------------------------------------ toast with action

	toast = (msg, action, fn) => {
		clearTimeout(this.toastTimer);
		this.update({ toast: { msg, action, fn } });
		this.toastTimer = setTimeout(() => this.update({ toast: null }), 5000);
	};

	// ------------------------------------------------------------------ derived

	tasksForClient = clientId => this.state.tasks.filter(t => t.clientId === clientId);

	serviceFor = client => {
		const { fService, selSvc } = this.state;
		if (fService) return fService;
		if (selSvc[client.id]) return selSvc[client.id];
		const tasks = this.tasksForClient(client.clientId);
		const first = SERVICE_ORDER.find(s => tasks.some(t => t.service === s));
		return first || "monthend";
	};

	periodFor = (client, service) => {
		const key = `${client.id}:${service}`;
		if (this.state.periods[key]) return this.state.periods[key];
		// Month-end closes the month that just ended; everything else works in the current period.
		if (service === "monthend") return shiftPeriod(service, periodFor(service, today()), -1);
		return periodFor(service, today());
	};

	passFilters = task => {
		const { fAssignee, fStatus, myUserId } = this.state;
		if (fAssignee) {
			const id = fAssignee === "me" ? myUserId : parseInt(fAssignee, 10);
			if (!assignees(task).includes(id)) return false;
		}
		if (fStatus === "overdue") return isOverdue(task);
		if (fStatus && task.status !== fStatus) return false;
		return true;
	};

	replaceTask = (task, extra = []) => {
		let tasks = this.state.tasks.map(t => (t.id === task.id ? task : t));
		extra.filter(Boolean).forEach(n => {
			if (!tasks.some(t => t.id === n.id)) tasks = tasks.concat([n]);
		});
		return this.update({ tasks });
	};

	// ------------------------------------------------------------------ handlers: layout

	setView = view => {
		this.update({ view, calendarFocusClientId: null });
	};

	onToggleClient = id => {
		const openClients = new Set(this.state.openClients);
		openClients.has(id) ? openClients.delete(id) : openClients.add(id);
		this.update({ openClients });
	};

	onServiceChange = (clientId, service) => {
		this.update({ selSvc: Object.assign({}, this.state.selSvc, { [clientId]: service }) });
	};

	onPeriodShift = (clientId, service, direction) => {
		const client = this.state.clients.find(c => c.id === clientId);
		const period = shiftPeriod(service, this.periodFor(client, service), direction);
		this.update({ periods: Object.assign({}, this.state.periods, { [`${clientId}:${service}`]: period }) });
	};

	showPeriod = (client, service, periodStart) => {
		const period = periodFor(service, periodStart);
		const openClients = new Set(this.state.openClients);
		openClients.add(client.id);
		this.update({
			openClients,
			selSvc: Object.assign({}, this.state.selSvc, { [client.id]: service }),
			periods: Object.assign({}, this.state.periods, { [`${client.id}:${service}`]: period }),
			fService: "",
		});
	};

	onOpenCalendar = client => {
		this.update({ view: "calendar", calendarFocusClientId: client ? client.clientId : null });
	};

	onGotoClient = (clientId, service) => {
		const client = this.state.clients.find(c => c.clientId === clientId);
		if (!client) return;
		const task = this.state.tasks.find(t => t.clientId === clientId && t.service === service);
		this.showPeriod(client, service, task ? task.periodStart : today());
		this.update({ view: "clients" }).then(() => {
			setTimeout(() => {
				const n = document.querySelector(`[data-client="${client.id}"]`);
				if (n) n.scrollIntoView({ behavior: "smooth", block: "start" });
			}, 50);
		});
	};

	// ------------------------------------------------------------------ handlers: tasks

	onToggleComplete = async task => {
		const toComplete = !isComplete(task);
		const result = await BackofficeService.updateTask({ taskId: task.id, status: toComplete ? "complete" : "in_progress" });
		if (!result.ok) return alert({ type: "error", text: result.error });
		await this.replaceTask(result.data.task, [result.data.nextTask]);
		await this.update({ flashTaskId: task.id });
		if (toComplete) {
			const parts = [`“${task.title}” marked complete`];
			if (result.data.nextTask) parts.push(`next ${(FREQUENCIES[task.frequency] || "").toLowerCase()} occurrence created`);
			if (task.emailAutomation && task.emailAutomation.status === "active") parts.push("email automation stopped");
			this.toast(parts.join(" · "), "Undo", async () => {
				const undo = await BackofficeService.updateTask({ taskId: task.id, status: "in_progress" });
				if (undo.ok) await this.replaceTask(undo.data.task);
			});
		}
	};

	onStatusChange = async (task, status) => {
		const result = await BackofficeService.updateTask({ taskId: task.id, status });
		if (!result.ok) return alert({ type: "error", text: result.error });
		await this.replaceTask(result.data.task, [result.data.nextTask]);
		await this.update({ flashTaskId: task.id });
		if (status === "complete" && result.data.nextTask) this.toast(`“${task.title}” marked complete · next ${(FREQUENCIES[task.frequency] || "").toLowerCase()} occurrence created`);
	};

	onDelete = async task => {
		const result = await BackofficeService.removeTask({ taskId: task.id });
		if (!result.ok) return alert({ type: "error", text: result.error });
		await this.update({ tasks: this.state.tasks.filter(t => t.id !== task.id) });
		this.toast(`Deleted “${task.title}”`, "Undo", async () => {
			const restored = await BackofficeService.restoreTask({ taskId: task.id });
			if (restored.ok) await this.update({ tasks: this.state.tasks.concat([restored.data]) });
			else await this.reloadTasks();
		});
	};

	onEdit = task => {
		const client = this.state.clients.find(c => c.clientId === task.clientId);
		this.update({ taskModal: { task, clientId: task.clientId, service: task.service, periodStart: task.periodStart, client } });
	};

	onAddTask = (client, service, period, date) => {
		this.update({ taskModal: { task: null, clientId: client ? client.clientId : null, service: service || "monthend", periodStart: period ? period.start : null, date: date || null, client } });
	};

	onTaskSaved = async ({ task, nextTask, created, emailWarning }) => {
		if (created) {
			const client = this.state.clients.find(c => c.clientId === task.clientId);
			await this.update({ tasks: this.state.tasks.concat([task]), taskModal: null });
			if (client) this.showPeriod(client, task.service, task.periodStart);
			else await this.load();
			this.toast(`Added “${task.title}” to ${client ? client.name : "client"} · ${SERVICES[task.service].label}${task.emailAutomation ? ` · ${task.emailAutomation.sends.filter(s => s.status === "scheduled").length} email sends marked on calendar` : ""}`);
		} else {
			await this.replaceTask(task, [nextTask]);
			await this.update({ taskModal: null, flashTaskId: task.id });
			this.toast(`Updated “${task.title}”`);
		}
		if (emailWarning) alert({ type: "error", text: `Email automation not activated: ${emailWarning}` });
	};

	// ------------------------------------------------------------------ handlers: accounts

	onToggleAccounts = task => {
		const openAccounts = new Set(this.state.openAccounts);
		openAccounts.has(task.id) ? openAccounts.delete(task.id) : openAccounts.add(task.id);
		this.update({ openAccounts });
	};

	onToggleAccount = async (task, account) => {
		const result = await BackofficeService.updateTaskAccount({ taskId: task.id, accountId: account.id, isDone: !account.isDone });
		if (!result.ok) return alert({ type: "error", text: result.error });
		await this.replaceTask(result.data);
		const updated = result.data;
		if (!account.isDone && updated.accounts.every(a => a.isDone) && !isComplete(updated)) {
			this.toast(`All ${updated.accounts.length} accounts done on “${task.title}”`, "Mark task complete", () => this.onStatusChange(updated, "complete"));
		}
	};

	onAddAccount = async (task, { name, type }) => {
		const result = await BackofficeService.addTaskAccount({ taskId: task.id, name, type });
		if (!result.ok) return alert({ type: "error", text: result.error });
		await this.replaceTask(result.data);
		this.toast(`Added ${name}`);
	};

	onRemoveAccount = async (task, account) => {
		const result = await BackofficeService.removeTaskAccount({ taskId: task.id, accountId: account.id });
		if (!result.ok) return alert({ type: "error", text: result.error });
		await this.replaceTask(result.data);
		this.toast(`Removed ${account.name} from this task`, "Undo", async () => {
			const back = await BackofficeService.addTaskAccount({ taskId: task.id, name: account.name, type: account.type, glCode: account.glCode, chartOfAccountId: account.chartOfAccountId });
			if (back.ok) await this.replaceTask(back.data);
		});
	};

	// ------------------------------------------------------------------ handlers: email

	onOpenEmail = task => this.update({ emailModalTaskId: task.id });

	onEmailSaved = async (task, message) => {
		await this.replaceTask(task);
		await this.update({ emailModalTaskId: null });
		if (message) this.toast(message);
	};

	onSkipSend = async (task, send) => {
		const result = await BackofficeService.skipEmailSend({ sendId: send.id });
		if (!result.ok) return alert({ type: "error", text: result.error });
		await this.replaceTask(result.data);
		this.toast("Send skipped. The follow-up cadence continues from the next date.");
	};

	refreshGmail = async () => {
		const gmailStatus = await GmailService.getStatus().catch(() => null);
		await this.update({ gmailStatus: gmailStatus || { connected: false, gmailAddress: null } });
	};

	// ------------------------------------------------------------------ handlers: templates & clients

	onOpenTemplate = (client, service, period) => {
		this.update({ templateModal: { clientId: client ? client.clientId : null, service: service || null, periodStart: period ? period.start : null } });
	};

	onTemplateApplied = async ({ added, period, tasks, service, clientId }) => {
		await this.update({ templateModal: null });
		await this.reloadTasks();
		const client = this.state.clients.find(c => c.clientId === clientId);
		if (!client) await this.load();
		const found = this.state.clients.find(c => c.clientId === clientId);
		if (found) this.showPeriod(found, service, period.start);
		this.toast(`Added ${added} ${SERVICES[service].label.toLowerCase()} task${added === 1 ? "" : "s"} to ${found ? found.name : "client"} for ${period.label}.`);
	};

	onClientAdded = async client => {
		await this.update({ showAddClient: false });
		const clients = await BackofficeService.listClients();
		if (clients.ok) {
			const openClients = new Set(this.state.openClients);
			openClients.add(client.id);
			await this.update({ clients: clients.data, openClients });
		}
		this.toast(`${client.name} added to backoffice. Prepopulate a service from a template to get started.`);
	};

	// ------------------------------------------------------------------ render

	renderTiles() {
		const { clients, tasks, myUserId } = this.state;
		const t0 = today();
		const wkEnd = addDays(t0, 7);
		const monthEnd = addDays(t0, 30);
		const open = tasks.filter(t => !isComplete(t));
		const due = open.filter(t => t.dueDate >= t0 && t.dueDate <= wkEnd).length;
		const overdue = tasks.filter(t => isOverdue(t)).length;
		const pending = tasks.filter(t => t.status === "pending_client").length;
		const emails = tasks.reduce((n, t) => n + (t.emailAutomation ? t.emailAutomation.sends.filter(s => s.status === "scheduled" && s.scheduledFor >= t0 && s.scheduledFor <= monthEnd).length : 0), 0);
		const mine = open.filter(t => assignees(t).includes(myUserId)).length;
		const tiles = [
			["Backoffice clients", clients.length, "Active this period", ""],
			["Due in next 7 days", due, "Across all services", ""],
			["Overdue", overdue, "Needs attention", overdue ? "warn" : ""],
			["Pending client", pending, "Email automations active", pending ? "amber" : ""],
			["Scheduled email sends", emails, "Next 30 days", ""],
			["Assigned to me", mine, "Open tasks where I am P, R or M", ""],
		];
		return (
			<div className="bo-tiles">
				{tiles.map(x => (
					<div key={x[0]} className="bo-tile">
						<span className="bo-tile__lbl">{x[0]}</span>
						<span className={clsx("bo-tile__val", x[3] && `bo-tile__val--${x[3]}`)}>{x[1]}</span>
						<span className="bo-tile__sub">{x[2]}</span>
					</div>
				))}
			</div>
		);
	}

	renderToolbar() {
		const { view, search, fService, fAssignee, fStatus, users } = this.state;
		return (
			<div className="bo-toolbar">
				<div className="bo-seg">
					<button className={clsx("ft-bo-view-clients", { "bo-seg__on": view === "clients" })} type="button" onClick={() => this.setView("clients")}>
						<I.list />
						Clients
					</button>
					<button className={clsx("ft-bo-view-calendar", { "bo-seg__on": view === "calendar" })} type="button" onClick={() => this.setView("calendar")}>
						<I.cal />
						Calendar
					</button>
					<button className={clsx("ft-bo-view-capacity", { "bo-seg__on": view === "capacity" })} type="button" onClick={() => this.setView("capacity")}>
						<I.clock />
						Capacity
					</button>
				</div>
				<div className="bo-filters">
					<div className="bo-search">
						<I.search />
						<input type="text" placeholder="Search backoffice clients" aria-label="Search clients" value={search} onChange={e => this.update({ search: e.target.value })} />
					</div>
					<select className="bo-fsel" value={fService} onChange={e => this.update({ fService: e.target.value })} aria-label="Filter by service">
						<option value="">All services</option>
						{SERVICE_ORDER.map(s => (
							<option key={s} value={s}>
								{SERVICES[s].label}
							</option>
						))}
					</select>
					<select className="bo-fsel" value={fAssignee} onChange={e => this.update({ fAssignee: e.target.value })} aria-label="Filter by assignee">
						<option value="">Everyone</option>
						<option value="me">My tasks</option>
						{users.map(u => (
							<option key={u.id} value={u.id}>
								{u.name}
							</option>
						))}
					</select>
					<select className="bo-fsel" value={fStatus} onChange={e => this.update({ fStatus: e.target.value })} aria-label="Filter by status">
						<option value="">Any status</option>
						{STATUS_ORDER.map(s => (
							<option key={s} value={s}>
								{STATUSES[s].label}
							</option>
						))}
						<option value="overdue">Overdue</option>
					</select>
				</div>
			</div>
		);
	}

	renderClients() {
		const { clients, search, users, flashTaskId, openAccounts, openClients } = this.state;
		const q = search.trim().toLowerCase();
		const visible = clients.filter(c => !q || (c.name || "").toLowerCase().includes(q) || (c.industry || "").toLowerCase().includes(q) || (c.operatingName || "").toLowerCase().includes(q));

		if (!clients.length) {
			return (
				<div className="bo-empty" style={{ marginTop: 16 }}>
					<b>No backoffice clients yet</b>Add a client to start tracking its payroll, month-end, AP or ad hoc work.
					<div className="bo-empty__row">
						<button className="bo-btn bo-btn--primary bo-btn--sm" type="button" onClick={() => this.update({ showAddClient: true })}>
							<I.plus />
							Add backoffice client
						</button>
					</div>
				</div>
			);
		}
		if (!visible.length) {
			return (
				<div className="bo-empty" style={{ marginTop: 16 }}>
					<b>No clients match “{search}”</b>
				</div>
			);
		}

		const handlers = {
			onServiceChange: this.onServiceChange,
			onPeriodShift: this.onPeriodShift,
			onOpenCalendar: this.onOpenCalendar,
			onOpenTemplate: this.onOpenTemplate,
			onAddTask: this.onAddTask,
			onToggleComplete: this.onToggleComplete,
			onStatusChange: this.onStatusChange,
			onEdit: this.onEdit,
			onDelete: this.onDelete,
			onOpenEmail: this.onOpenEmail,
			onToggleAccounts: this.onToggleAccounts,
			onToggleAccount: this.onToggleAccount,
			onAddAccount: this.onAddAccount,
			onRemoveAccount: this.onRemoveAccount,
		};

		return (
			<div className="bo-clients">
				{visible.map(client => {
					const service = this.serviceFor(client);
					return (
						<ClientCard
							key={client.id}
							client={client}
							tasks={this.tasksForClient(client.clientId)}
							users={users}
							open={openClients.has(client.id)}
							service={service}
							period={this.periodFor(client, service)}
							passFilters={this.passFilters}
							flashTaskId={flashTaskId}
							openAccounts={openAccounts}
							onToggle={this.onToggleClient}
							handlers={handlers}
						/>
					);
				})}
			</div>
		);
	}

	render() {
		const { loading, error, view, clients, tasks, users, templates, gmailStatus, taskModal, emailModalTaskId, showAccountModal, templateModal, showAddClient, toast, fService, calendarFocusClientId } = this.state;
		const emailTask = emailModalTaskId ? tasks.find(t => t.id === emailModalTaskId) : null;
		const emailClient = emailTask ? clients.find(c => c.clientId === emailTask.clientId) : null;

		return (
			<div className="bo-page">
				<div className="bo-head">
					<div>
						<div className="bo-crumbs">
							Workflow / <b>Backoffice</b>
						</div>
						<h1>Backoffice</h1>
						<p>Recurring payroll, month-end bookkeeping, AP and ad hoc work for backoffice clients. Tasks sync to the calendar and to client email automations.</p>
					</div>
					<div className="bo-head__actions">
						<button className={clsx("bo-email-chip", { "bo-email-chip--off": !gmailStatus.connected })} type="button" title="Connected email account" onClick={() => this.update({ showAccountModal: true })}>
							<span className="bo-email-chip__prov">G</span>
							<span className="bo-email-chip__dot" />
							<span>{gmailStatus.connected ? `${gmailStatus.gmailAddress} · Google Workspace` : "No email account connected"}</span>
						</button>
						<button className="bo-btn" type="button" onClick={() => this.update({ showAccountModal: true })}>
							<I.mail />
							Email account
						</button>
						<button className="bo-btn" type="button" onClick={() => this.update({ showAddClient: true })}>
							<I.briefcase />
							Add client
						</button>
						<button className="bo-btn ft-bo-template-top" type="button" onClick={() => this.onOpenTemplate(null, null, null)} disabled={!clients.length}>
							<I.file />
							Prepopulate from template
						</button>
						<button className="bo-btn bo-btn--primary ft-bo-new-task" type="button" onClick={() => this.onAddTask(clients[0] || null, "monthend", null, today())} disabled={!clients.length}>
							<I.plusPlain />
							New task
						</button>
					</div>
				</div>

				{loading && <div className="bo-loading">Loading backoffice…</div>}
				{!loading && error && (
					<div className="bo-empty" style={{ marginTop: 16 }}>
						<b>Could not load backoffice</b>
						{error}
						<div className="bo-empty__row">
							<button className="bo-btn bo-btn--sm" type="button" onClick={this.load}>
								Retry
							</button>
						</div>
					</div>
				)}

				{!loading && !error && (
					<>
						{this.renderTiles()}
						{this.renderToolbar()}

						{view === "clients" && <section className="bo-view">{this.renderClients()}</section>}
						{view === "calendar" && (
							<section className="bo-view">
								<CalendarView
									clients={clients}
									tasks={tasks}
									users={users}
									serviceFilter={fService}
									passFilters={this.passFilters}
									focusClientId={calendarFocusClientId}
									gmailAddress={gmailStatus.gmailAddress}
									onCreate={(date, client) => this.onAddTask(client || clients[0], "monthend", null, date)}
									onToggleComplete={this.onToggleComplete}
									onEdit={this.onEdit}
									onOpenEmail={this.onOpenEmail}
									onSkipSend={this.onSkipSend}
									onGotoClient={this.onGotoClient}
								/>
							</section>
						)}
						{view === "capacity" && (
							<section className="bo-view">
								<CapacityView clients={clients} tasks={tasks} users={users} serviceFilter={fService} assigneeFilter={this.state.fAssignee} myUserId={this.state.myUserId} passFilters={this.passFilters} onCapacityChange={this.load} />
							</section>
						)}
					</>
				)}

				{taskModal && (
					<TaskModal
						task={taskModal.task}
						clients={clients}
						users={users}
						initialClientId={taskModal.clientId}
						initialService={taskModal.service}
						initialDate={taskModal.date}
						periodStart={taskModal.periodStart}
						gmailStatus={gmailStatus}
						onClose={() => this.update({ taskModal: null })}
						onSaved={this.onTaskSaved}
					/>
				)}
				{emailTask && (
					<EmailAutomationModal
						task={emailTask}
						client={emailClient}
						users={users}
						gmailStatus={gmailStatus}
						onClose={() => this.update({ emailModalTaskId: null })}
						onSaved={this.onEmailSaved}
						onSwitchAccount={() => this.update({ emailModalTaskId: null, showAccountModal: true })}
					/>
				)}
				{showAccountModal && <EmailAccountModal gmailStatus={gmailStatus} onClose={() => this.update({ showAccountModal: false })} onChanged={this.refreshGmail} onTasksResynced={this.load} />}
				{templateModal && (
					<TemplateModal
						clients={clients}
						users={users}
						templates={templates}
						initialClientId={templateModal.clientId}
						initialService={templateModal.service}
						initialPeriodStart={templateModal.periodStart}
						myUserId={this.state.myUserId}
						onClose={() => this.update({ templateModal: null })}
						onApplied={this.onTemplateApplied}
					/>
				)}
				{showAddClient && <AddClientModal existing={clients} onClose={() => this.update({ showAddClient: false })} onAdded={this.onClientAdded} />}

				{toast && (
					<div className="bo-toast" role="status">
						<span>{toast.msg}</span>
						{toast.action && (
							<button
								type="button"
								onClick={() => {
									const fn = toast.fn;
									this.update({ toast: null });
									if (fn) fn();
								}}
							>
								{toast.action}
							</button>
						)}
					</div>
				)}
			</div>
		);
	}
}

export default Backoffice;
